import React from 'react';
import { motion } from 'framer-motion';
import {
  BanknotesIcon,
  ClockIcon,
  LockClosedIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import TrustTierBadge from './TrustTierBadge';

function MiniCard({ icon: Icon, iconColor, iconBg, label, amount, count, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-gray-50/80 dark:bg-[#0A0A0A] rounded-xl border border-gray-100 dark:border-[#1A1A1A] p-3"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
        <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-[#555555] font-medium">
          {label}
        </p>
      </div>
      <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
        ${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
      </p>
      {count > 0 && (
        <p className="text-[10px] text-gray-400 dark:text-[#666666] mt-0.5">
          {count} payment{count !== 1 ? 's' : ''}
        </p>
      )}
    </motion.div>
  );
}

export default function BalanceOverview({ balanceData, isLoading }) {
  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-20 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl" />
        <div className="grid grid-cols-2 gap-2">
          <div className="h-16 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl" />
          <div className="h-16 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl" />
        </div>
      </div>
    );
  }

  if (!balanceData) return null;

  const { stripe, payout, riskProfile } = balanceData;

  return (
    <div className="space-y-3">
      {/* Available Balance */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-[#111111] rounded-xl border border-gray-100 dark:border-[#1A1A1A] p-4"
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-[#555555] font-medium">
            Available
          </p>
          <TrustTierBadge tier={riskProfile?.trustTier || 0} />
        </div>
        <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
          ${(stripe?.available || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </p>
        {riskProfile?.accountFrozen && (
          <div className="mt-2 flex items-center gap-1.5">
            <LockClosedIcon className="w-3.5 h-3.5 text-red-500" />
            <span className="text-xs text-red-600 dark:text-red-400">Payouts frozen</span>
          </div>
        )}
      </motion.div>

      {/* Breakdown Grid */}
      <div className="grid grid-cols-2 gap-2">
        {(payout?.pendingAmount > 0 || payout?.pendingCount > 0) && (
          <MiniCard
            icon={ClockIcon}
            iconColor="text-amber-500"
            label="Pending"
            amount={payout.pendingAmount}
            count={payout.pendingCount}
            delay={0.03}
          />
        )}

        {(payout?.underReviewAmount > 0 || payout?.underReviewCount > 0) && (
          <MiniCard
            icon={ShieldCheckIcon}
            iconColor="text-blue-500"
            label="Review"
            amount={payout.underReviewAmount}
            count={payout.underReviewCount}
            delay={0.06}
          />
        )}

        {(payout?.reserveAmount || 0) > 0 && (
          <MiniCard
            icon={BanknotesIcon}
            iconColor="text-gray-500 dark:text-[#999999]"
            label="Reserve"
            amount={payout.reserveAmount}
            delay={0.09}
          />
        )}

        {(payout?.frozenAmount || 0) > 0 && (
          <MiniCard
            icon={LockClosedIcon}
            iconColor="text-red-500"
            label="Frozen"
            amount={payout.frozenAmount}
            count={payout.frozenCount}
            delay={0.12}
          />
        )}
      </div>

      {/* Next payout + delay info */}
      <div className="space-y-1 px-0.5">
        {payout?.nextEligibleDate && (
          <p className="text-[10px] text-gray-400 dark:text-[#666666] flex items-center gap-1.5">
            <ClockIcon className="w-3 h-3" />
            Next eligible:{' '}
            {new Date(
              payout.nextEligibleDate.toDate
                ? payout.nextEligibleDate.toDate()
                : payout.nextEligibleDate
            ).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </p>
        )}
        {riskProfile && (
          <p className="text-[10px] text-gray-400 dark:text-[#666666]">
            {riskProfile.payoutDelayDays}d delay · {riskProfile.tierName}
          </p>
        )}
      </div>
    </div>
  );
}
